import { useEffect, useRef, useState } from 'react';

import { useMedia } from 'react-use';

import { isToolIconObject, type ToolIconConfig } from '@/lib/constants/tool-ui-icons';

import type { LucideIcon } from 'lucide-react';

const DEFAULT_TRANSITION_DURATION_MS = 300;
/** Per-tick random cadence range. Every rotation rolls a fresh delay in
 *  `[MIN, MAX]` so parallel siblings never beat in unison and even a single
 *  spinner doesn't feel metronomic. */
const DEFAULT_INTERVAL_MIN_MS = 800;
const DEFAULT_INTERVAL_MAX_MS = 1000;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface UseRotatingIconOptions {
  icons: ToolIconConfig[];
  /**
   * ms between icon swaps. When set, this exact value is used for every
   * tick (deterministic — used by tests). When omitted, each tick picks a
   * fresh random value in `[800, 1000]` so parallel rows drift apart and
   * the cadence itself feels organic rather than metronomic.
   */
  intervalMs?: number;
  /** ms the fade-out lasts before the next icon takes its place. */
  transitionMs?: number;
  /**
   * Starting index in `icons`. Default: a random position. Combined with
   * the per-tick randomized interval, parallel rows mount on different
   * icons AND swap at different moments.
   */
  initialIndex?: number;
}

interface RotatingIconState {
  /** Resolved icon component for the current index, or null if `icons` is empty. */
  Icon: LucideIcon | null;
  /** True when the current icon should be flipped horizontally. */
  flip: boolean;
  /** True during the fade-out window so callers can apply the exit class. */
  isTransitioning: boolean;
}

/**
 * Cycles through `icons` with a fade-in/out transition. The wait between
 * swaps is randomized per-tick (when no explicit `intervalMs` is set), so
 * a row never repeats the same cadence twice and parallel siblings stay
 * permanently out of phase. When `icons.length <= 1` no timer is ever
 * scheduled, and none under reduced motion either: the global reset only removes the crossfade, so
 * the glyph would still jump to the next one every second.
 */
export function useRotatingIcon({
  icons,
  intervalMs,
  transitionMs = DEFAULT_TRANSITION_DURATION_MS,
  initialIndex,
}: UseRotatingIconOptions): RotatingIconState {
  // Lazy-initialized so the random index is captured once on mount and
  // stable across re-renders.
  const [index, setIndex] = useState(() => {
    if (initialIndex !== undefined) return initialIndex;
    if (icons.length <= 1) return 0;
    return Math.floor(Math.random() * icons.length);
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Two separate timers per cycle: outer wait (until next swap) and inner
  // fade-out (until index increments). Both refs are cleared on unmount so
  // we don't setState on a dead component.
  const waitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reduceMotion = useMedia(REDUCED_MOTION_QUERY, false);

  useEffect(() => {
    if (icons.length <= 1 || reduceMotion) return;
    let cancelled = false;

    const nextDelay = (): number => {
      if (intervalMs !== undefined) return intervalMs;
      const span = DEFAULT_INTERVAL_MAX_MS - DEFAULT_INTERVAL_MIN_MS;
      return DEFAULT_INTERVAL_MIN_MS + Math.floor(Math.random() * (span + 1));
    };

    const scheduleNext = () => {
      if (cancelled) return;
      waitTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        setIsTransitioning(true);
        transitionTimerRef.current = setTimeout(() => {
          if (cancelled) return;
          setIndex(prev => (prev + 1) % icons.length);
          setIsTransitioning(false);
          transitionTimerRef.current = null;
          scheduleNext();
        }, transitionMs);
      }, nextDelay());
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (waitTimerRef.current !== null) {
        clearTimeout(waitTimerRef.current);
        waitTimerRef.current = null;
      }
      if (transitionTimerRef.current !== null) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [icons.length, intervalMs, transitionMs, reduceMotion]);

  // Reduced motion can switch on mid-fade, which cancels the timer that would end it.
  const fading = isTransitioning && !reduceMotion;
  const config = icons[index];
  if (!config) return { Icon: null, flip: false, isTransitioning: fading };
  return {
    Icon: isToolIconObject(config) ? config.icon : config,
    flip: isToolIconObject(config) && !!config.flipHorizontal,
    isTransitioning: fading,
  };
}
